import React from 'react'
import { Calendar, MapPin, ExternalLink } from 'lucide-react'
import { experiences } from '../data/portfolio'
import { useScrollAnimation } from '../hooks/useScrollAnimation'

const Experience: React.FC = () => {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation()

  const formatDate = (dateString: string) => {
    if (dateString === 'Present') return 'Present'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short' 
    })
  }

  const calculateDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate)
    const end = endDate === 'Present' ? new Date() : new Date(endDate)
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30))
    
    if (diffMonths < 12) {
      return `${diffMonths} month${diffMonths > 1 ? 's' : ''}`
    } else {
      const years = Math.floor(diffMonths / 12)
      const months = diffMonths % 12
      return `${years} year${years > 1 ? 's' : ''}${months > 0 ? ` ${months} month${months > 1 ? 's' : ''}` : ''}`
    }
  }

  return (
    <section id="experience" className="section-padding bg-white dark:bg-slate-900">
      <div className="container-max">
        <div 
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900 dark:text-white">
            Professional Experience
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
            6+ years of experience in backend development, AI applications, and technical leadership
          </p>
        </div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 md:left-1/2 md:transform md:-translate-x-px top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-purple-500 to-blue-500"></div>

          {/* Experience items */}
          <div className="space-y-12">
            {experiences.map((experience, index) => {
              const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 })
              const isEven = index % 2 === 0

              return (
                <div 
                  key={experience.id}
                  ref={ref}
                  className={`relative transition-all duration-700 delay-${index * 100} ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-4 md:left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 border-4 border-white dark:border-slate-900 rounded-full shadow-lg z-10"></div>

                  {/* Content card */}
                  <div className={`ml-12 md:ml-0 md:w-5/12 ${isEven ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'}`}>
                    <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 card-hover">
                      {/* Header */}
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                            {experience.position}
                          </h3>
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                              {experience.company}
                            </h4>
                            {experience.companyLogo && (
                              <ExternalLink size={16} className="text-slate-400" />
                            )}
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-slate-500 dark:text-slate-400">
                            <div className="flex items-center space-x-1">
                              <MapPin size={14} />
                              <span>{experience.location}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Calendar size={14} />
                              <span>
                                {formatDate(experience.startDate)} - {formatDate(experience.endDate)}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Duration badge */}
                        <div className="mt-3 sm:mt-0 sm:ml-4">
                          <span className="inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full">
                            {calculateDuration(experience.startDate, experience.endDate)}
                          </span>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="mb-4">
                        <ul className="space-y-2">
                          {experience.description.map((item, idx) => (
                            <li key={idx} className="text-slate-600 dark:text-slate-300 leading-relaxed flex items-start">
                              <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Technologies */}
                      <div>
                        <h5 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
                          Technologies & Skills:
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          {experience.technologies.map((tech, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-1 bg-gray-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs rounded-md font-medium"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Arrow pointing to timeline (desktop only) */}
                    <div className={`hidden md:block absolute top-8 ${
                      isEven 
                        ? 'right-0 transform translate-x-full' 
                        : 'left-0 transform -translate-x-full'
                    }`}>
                      <div className={`w-0 h-0 ${
                        isEven
                          ? 'border-l-8 border-l-white dark:border-l-slate-800 border-y-8 border-y-transparent'
                          : 'border-r-8 border-r-white dark:border-r-slate-800 border-y-8 border-y-transparent'
                      }`}></div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Call to action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-8 rounded-xl border border-blue-200 dark:border-blue-800">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
              Interested in working together?
            </h3>
            <p className="text-slate-600 dark:text-slate-300 mb-4">
              Let's discuss how my experience can contribute to your next project.
            </p>
            <a 
              href="#contact" 
              className="btn-primary inline-block"
            >
              Get In Touch
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Experience